/**
 * Spec 082 — typed API client for rule-ingestion admin routes.
 *
 * Thin wrappers over `apiFetch` + `apiGet`. Exports one function per admin
 * API endpoint — downstream pages use these directly instead of hand-rolling
 * fetch calls, so 401 redirect behavior stays consistent.
 */
import { apiFetch, apiGet } from '../lib/api.js';

export type AuthorConfidence = 'high' | 'medium' | 'low';
export type CandidateReviewStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'superseded'
  | 'clustered';
export type SuggestionReviewStatus = 'pending' | 'approved' | 'rejected';
export type SuggestionType =
  | 'provenance_only'
  | 'rubric_change'
  | 'evaluation_steps_change'
  | 'definition_refinement';
export type MediumSourceType = 'tag_feed' | 'author_feed';

export interface CandidateRule {
  id: string;
  job_id: string;
  source_post_url: string;
  source_post_title: string | null;
  source_post_author: string | null;
  source_post_published_at: string | null;
  criterion: string;
  definition: string;
  evaluation_approach: string;
  supporting_quote: string;
  author_confidence: AuthorConfidence;
  engines_mentioned: string[];
  content_types_mentioned: string[];
  embedding_model: string;
  review_status: CandidateReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_rule_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface RuleSuggestion {
  id: string;
  job_id: string;
  existing_rule_id: string;
  source_post_url: string;
  source_post_title: string | null;
  source_post_author: string | null;
  suggestion_type: SuggestionType;
  suggested_change: string;
  proposed_patch: Record<string, unknown>;
  supporting_quote: string;
  similarity_score: number;
  review_status: SuggestionReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
  // Spec 102 — target-rule enrichment (LEFT JOIN to current-version rules).
  // All null when the target rule no longer exists / is no longer current.
  existing_rule_slug: string | null;
  existing_rule_name: string | null;
  existing_rule_status: string | null;
  existing_rule_definition: string | null;
  /** Current rules.evaluation_steps JSONB ({ criteria, evaluation_steps, rubric }). */
  existing_rule_evaluation_steps: Record<string, unknown> | null;
}

export interface MediumSource {
  id: string;
  type: MediumSourceType;
  identifier: string;
  rss_url: string;
  is_active: boolean;
  last_fetched_at: string | null;
  trust_weight: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Paginated<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

/* ─── Candidates ───────────────────────────────────────────────── */

export async function listCandidates(filters: {
  status?: CandidateReviewStatus;
  author?: string;
  confidence?: AuthorConfidence;
  /**
   * Phase 9 Session D (gap 082-1) — reserved param. The backend route
   * currently declares `source_id` in its query type but does not yet
   * apply it in SQL. We forward it for forward-compat so the wire
   * contract is stable once the server honors it.
   * TODO(backend): push filter to SQL when `candidate_rules` exposes a
   * joined `source_id` column via `rule_ingestion_jobs`.
   */
  source_id?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<Paginated<CandidateRule>> {
  const q = new URLSearchParams();
  if (filters.status) q.set('status', filters.status);
  if (filters.author) q.set('author', filters.author);
  if (filters.confidence) q.set('confidence', filters.confidence);
  if (filters.source_id) q.set('source_id', filters.source_id);
  if (filters.limit !== undefined) q.set('limit', String(filters.limit));
  if (filters.offset !== undefined) q.set('offset', String(filters.offset));
  const url = `/api/v1/admin/rules/candidates${q.size ? `?${q}` : ''}`;
  return apiGet<Paginated<CandidateRule>>(url);
}

export async function getCandidate(id: string): Promise<CandidateRule> {
  const res = await apiGet<{ data: CandidateRule }>(`/api/v1/admin/rules/candidates/${id}`);
  return res.data;
}

export interface ApproveCandidateBody {
  criterion?: string;
  definition?: string;
  evaluation_approach?: string;
  epistemic_status?: string;
  engines_mentioned?: string[];
  content_types_mentioned?: string[];
  slug?: string;
  scoring_mode?: 'g_eval' | 'dag';
}

export async function approveCandidate(
  id: string,
  body: ApproveCandidateBody = {},
): Promise<{ candidate_id: string; rule_id: string; rule_slug: string }> {
  const res = await apiFetch(`/api/v1/admin/rules/candidates/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Approve failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: { candidate_id: string; rule_id: string; rule_slug: string } };
  return json.data;
}

export async function rejectCandidate(id: string, reason?: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/rules/candidates/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Reject failed: ${res.status} ${text.slice(0, 200)}`);
  }
}

export interface BulkRejectResult {
  rejected_count: number;
  rejected_ids: string[];
  skipped_ids: string[];
}

/**
 * Spec 092 — bulk reject N pending candidates in one transaction. Non-pending
 * and non-existent ids are silently skipped and reported via `skipped_ids` so
 * the caller can craft an appropriate toast message.
 */
export async function bulkRejectCandidates(
  ids: string[],
  reason?: string,
): Promise<BulkRejectResult> {
  const res = await apiFetch(`/api/v1/admin/rules/candidates/bulk-reject`, {
    method: 'POST',
    body: JSON.stringify(reason ? { ids, reason } : { ids }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Bulk reject failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: BulkRejectResult };
  return json.data;
}

export interface RescanResult {
  scanned: number;
  moved_to_suggestions: number;
  no_hit_left_as_candidate: number;
  errors: number;
  /**
   * Spec 101 — the threshold the backend actually used for this rescan call
   * (default 0.85 when the body omitted the field). The frontend uses this
   * to decide whether to append `· threshold X.XX` to the success toast.
   */
  threshold_used: number;
  duration_ms: number;
}

/**
 * Spec 099 — UI affordance over spec 098's rescan route.
 * Spec 101 — accepts an optional `{ threshold }` argument forwarded to the
 * backend, which validates it to `[0.50, 0.95]` and uses it instead of the
 * 0.85 default. Existing zero-arg callers continue to work via the default
 * `{}` parameter object.
 */
export async function rescanCandidates(
  opts: { threshold?: number } = {},
): Promise<RescanResult> {
  const body: Record<string, unknown> = {};
  if (opts.threshold !== undefined) body['threshold'] = opts.threshold;
  const res = await apiFetch(`/api/v1/admin/rules/candidates/rescan`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Rescan failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: RescanResult };
  return json.data;
}

/* ─── Spec 101 — Nearest rules preview ─────────────────────────── */

export interface NearestRuleMatch {
  rule_id: string;
  slug: string;
  name: string;
  /** Cosine similarity in [0.0, 1.0], rounded server-side to 3 decimals. */
  similarity: number;
}

export interface NearestRulesResponse {
  candidate_id: string;
  embedding_model: string;
  matches: NearestRuleMatch[];
}

/**
 * Spec 101 — fetches the top-3 nearest seeded rules for a candidate so the
 * operator can see actual cosine similarities (not just a "Moved 0" toast).
 * Returns matches=[] when the candidate has no embedding OR no rule has a
 * matching embedding_model — the panel renders an empty-state in that case.
 */
export async function getNearestRules(
  candidateId: string,
): Promise<NearestRulesResponse> {
  const res = await apiGet<{ data: NearestRulesResponse }>(
    `/api/v1/admin/rules/candidates/${candidateId}/nearest-rules`,
  );
  return res.data;
}

export interface BackfillEmbeddingsResult {
  updated: number;
  failed: number;
  total_candidates: number;
  duration_ms: number;
}

/**
 * Spec 100 — Count of rules currently missing embeddings (drives the
 * `BackfillEmbeddingsBanner` on /admin/rules). Same `requireRuleAdmin`
 * gate as `backfillRuleEmbeddings`. Throws on non-2xx (the 401 redirect
 * is handled by `apiFetch`/`apiGet`).
 */
export async function getMissingEmbeddingsCount(): Promise<number> {
  const res = await apiGet<{ data: { count: number } }>(
    `/api/v1/admin/rules/embeddings/missing-count`,
  );
  return res.data.count;
}

/**
 * Spec 100 — UI affordance over the spec 097 backfill route. Calls
 * `POST /api/v1/admin/rules/embeddings/backfill` with an empty body and
 * surfaces the {updated, failed, total_candidates, duration_ms} counts so
 * the caller can render a toast. Throws Error on non-2xx, matching the
 * rescanCandidates/bulkRejectCandidates pattern.
 */
export async function backfillRuleEmbeddings(): Promise<BackfillEmbeddingsResult> {
  const res = await apiFetch(`/api/v1/admin/rules/embeddings/backfill`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Backfill failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: BackfillEmbeddingsResult };
  return json.data;
}

/* ─── Rule-Set Health (spec 104) ───────────────────────────────── */

export interface HealthRuleRef {
  id: string;
  slug: string;
  name: string;
}

export interface RedundantPair {
  rule_a: HealthRuleRef;
  rule_b: HealthRuleRef;
  similarity: number;
}

export interface CoverageGap {
  proposal_id: string;
  label: string;
  nearest_rule_similarity: number;
}

export interface LowUtilityRule {
  rule_id: string;
  slug: string;
  name: string;
  pass_rate: number;
  evaluation_count: number;
  insufficient_data: boolean;
}

export interface RuleSetHealth {
  redundant_pairs: RedundantPair[];
  coverage_gaps: CoverageGap[];
  low_utility_rules: LowUtilityRule[];
  thresholds: { redundancy: number; coverage: number; min_n: number };
}

/**
 * Spec 104 — read-only rule-set health metrics. Same `requireRuleAdmin` gate
 * as the other admin-rules routes. Throws on non-2xx (401 redirect handled by
 * `apiGet`).
 */
export async function getRuleSetHealth(): Promise<RuleSetHealth> {
  const res = await apiGet<{ data: RuleSetHealth }>(
    `/api/v1/admin/rules/health`,
  );
  return res.data;
}

/* ─── Suggestions ──────────────────────────────────────────────── */

export async function listSuggestions(filters: {
  status?: SuggestionReviewStatus;
  existing_rule_id?: string;
  suggestion_type?: SuggestionType;
  limit?: number;
  offset?: number;
} = {}): Promise<Paginated<RuleSuggestion>> {
  const q = new URLSearchParams();
  if (filters.status) q.set('status', filters.status);
  if (filters.existing_rule_id) q.set('existing_rule_id', filters.existing_rule_id);
  if (filters.suggestion_type) q.set('suggestion_type', filters.suggestion_type);
  if (filters.limit !== undefined) q.set('limit', String(filters.limit));
  if (filters.offset !== undefined) q.set('offset', String(filters.offset));
  const url = `/api/v1/admin/rules/suggestions${q.size ? `?${q}` : ''}`;
  return apiGet<Paginated<RuleSuggestion>>(url);
}

export async function getSuggestion(id: string): Promise<RuleSuggestion> {
  const res = await apiGet<{ data: RuleSuggestion }>(`/api/v1/admin/rules/suggestions/${id}`);
  return res.data;
}

export async function approveSuggestion(
  id: string,
  proposedPatch?: Record<string, unknown>,
): Promise<{
  suggestion_id: string;
  rule_id: string;
  kind: 'provenance_only' | 'version_bump';
  rule_version_new?: number;
  rule_version_prev?: number;
}> {
  const res = await apiFetch(`/api/v1/admin/rules/suggestions/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify(proposedPatch ? { proposed_patch: proposedPatch } : {}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Approve failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as {
    data: {
      suggestion_id: string;
      rule_id: string;
      kind: 'provenance_only' | 'version_bump';
      rule_version_new?: number;
      rule_version_prev?: number;
    };
  };
  return json.data;
}

export async function rejectSuggestion(id: string, reason?: string): Promise<void> {
  const res = await apiFetch(`/api/v1/admin/rules/suggestions/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Reject failed: ${res.status} ${text.slice(0, 200)}`);
  }
}

/* ─── Spec 103 — Candidate clustering + rule proposals ─────────── */

export type RuleProposalStatus = 'pending' | 'approved' | 'rejected';

export interface RuleProposalMember {
  candidate_id: string;
  member_similarity: number;
  criterion: string;
  definition?: string;
  supporting_quote?: string;
  source_post_url: string;
  source_post_author: string | null;
  source_post_title?: string | null;
}

export interface RuleProposal {
  id: string;
  status: RuleProposalStatus;
  proposed_name: string;
  proposed_definition: string;
  proposed_evaluation_steps: Record<string, unknown>;
  proposed_content_types: string[];
  cohesion: number;
  embedding_model: string;
  overlaps_existing_rule_id: string | null;
  overlaps_existing_rule_similarity: number | null;
  overlaps_existing_rule_slug: string | null;
  overlaps_existing_rule_name: string | null;
  created_rule_id: string | null;
  synthesis_source: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  member_count?: number;
  members?: RuleProposalMember[];
}

export interface ClusterResult {
  candidates_considered: number;
  clusters_formed: number;
  proposals_created: number;
  singletons_left: number;
  synthesis_llm_calls: number;
  synthesis_heuristic_calls: number;
  distance_threshold: number;
  min_cluster_size: number;
  duration_ms: number;
}

/**
 * Spec 103 — cluster pending candidates into proposed new rules. Optional
 * operator-tunable distance threshold (cosine distance, [0.05, 0.95]) and
 * min cluster size ([2, 50]). Throws on non-2xx (mirrors rescanCandidates).
 */
export async function clusterCandidates(
  opts: { distance_threshold?: number; min_cluster_size?: number } = {},
): Promise<ClusterResult> {
  const body: Record<string, unknown> = {};
  if (opts.distance_threshold !== undefined) body['distance_threshold'] = opts.distance_threshold;
  if (opts.min_cluster_size !== undefined) body['min_cluster_size'] = opts.min_cluster_size;
  const res = await apiFetch(`/api/v1/admin/rules/candidates/cluster`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Cluster failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: ClusterResult };
  return json.data;
}

export async function listProposals(filters: {
  status?: RuleProposalStatus;
  limit?: number;
  offset?: number;
} = {}): Promise<Paginated<RuleProposal>> {
  const q = new URLSearchParams();
  if (filters.status) q.set('status', filters.status);
  if (filters.limit !== undefined) q.set('limit', String(filters.limit));
  if (filters.offset !== undefined) q.set('offset', String(filters.offset));
  const url = `/api/v1/admin/rules/proposals${q.size ? `?${q}` : ''}`;
  return apiGet<Paginated<RuleProposal>>(url);
}

export async function getProposal(id: string): Promise<RuleProposal> {
  const res = await apiGet<{ data: RuleProposal }>(`/api/v1/admin/rules/proposals/${id}`);
  return res.data;
}

export async function approveProposal(
  id: string,
): Promise<{ proposal_id: string; rule_id: string | null; rule_slug?: string; idempotent?: boolean }> {
  const res = await apiFetch(`/api/v1/admin/rules/proposals/${id}/approve`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Approve failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as {
    data: { proposal_id: string; rule_id: string | null; rule_slug?: string; idempotent?: boolean };
  };
  return json.data;
}

export async function rejectProposal(
  id: string,
): Promise<{ proposal_id: string; status: string; members_released: number }> {
  const res = await apiFetch(`/api/v1/admin/rules/proposals/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Reject failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as {
    data: { proposal_id: string; status: string; members_released: number };
  };
  return json.data;
}

/* ─── Sources ──────────────────────────────────────────────────── */

export async function listSources(
  filters: { is_active?: boolean } = {},
): Promise<MediumSource[]> {
  const q = new URLSearchParams();
  if (filters.is_active !== undefined) q.set('is_active', String(filters.is_active));
  const url = `/api/v1/admin/rule-sources${q.size ? `?${q}` : ''}`;
  const res = await apiGet<{ data: MediumSource[] }>(url);
  return res.data;
}

export async function createSource(input: {
  type: MediumSourceType;
  identifier: string;
  trust_weight?: number;
  notes?: string | null;
  is_active?: boolean;
}): Promise<MediumSource> {
  const res = await apiFetch(`/api/v1/admin/rule-sources`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Create source failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: MediumSource };
  return json.data;
}

export async function updateSource(
  id: string,
  patch: { is_active?: boolean; trust_weight?: number; notes?: string | null },
): Promise<MediumSource> {
  const res = await apiFetch(`/api/v1/admin/rule-sources/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Update source failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const json = (await res.json()) as { data: MediumSource };
  return json.data;
}
